/**
 * @file Hook de synchronisation fire-and-forget du brouillon de parcours de réservation.
 * @module packages.common.components.table-reservation.hooks.useReservationDraftSync
 *
 * Trace côté backend les parcours où le client a saisi des coordonnées valides
 * sans finaliser : dès l'étape CONTACT (pour capturer le client qui remplit le
 * formulaire puis ferme l'onglet sans cliquer « Suivant »), puis à chaque
 * progression vers DETAILS et PAYMENT pour faire avancer l'étape d'abandon.
 *
 * Purement informatif et strictement non bloquant : debounce sur la saisie,
 * déduplication par clé de contenu, erreurs avalées (un échec de sync ne doit
 * jamais perturber le parcours), et hook inerte si l'adapter hôte n'expose pas
 * `syncReservationDraft`. La conversion du brouillon après finalisation est
 * gérée côté backend (draftId embarqué dans le payload de création).
 *
 * Le signal empreinte (`formData.paymentSignal`, cf. usePaymentSignalTracker)
 * voyage dans le même payload : un changement de signal part sans debounce,
 * car il survient typiquement juste avant que le client ferme l'onglet.
 */
import {useEffect, useRef} from 'react';
import {logger} from 'packages/common/utils/clientLogger';
import {useReservationData} from '../context/ReservationDataContext';
import {BASE_STEPS} from '../constants/steps';
import {buildReservationDateTime} from '../utils/buildReservationDateTime';
import {hasValidContact} from '../utils/contactValidity';

/** Étapes pendant lesquelles le brouillon est synchronisé */
const SYNC_STEPS = [BASE_STEPS.CONTACT, BASE_STEPS.DETAILS, BASE_STEPS.PAYMENT];

/**
 * Délai après le dernier changement avant l'envoi : absorbe la frappe en cours
 * à l'étape CONTACT et les allers-retours rapides entre étapes.
 */
const SYNC_DEBOUNCE_MS = 1500;

/**
 * Synchronise le brouillon du parcours courant avec le backend.
 *
 * @param {Object} params - Paramètres du hook
 * @param {Object} params.formData - État du formulaire de réservation (draftId inclus)
 * @param {string} params.currentStep - Étape logique courante (BASE_STEPS)
 * @param {string} params.currentLanguage - Langue courante du parcours client
 */
export function useReservationDraftSync({formData, currentStep, currentLanguage}) {
  const {syncReservationDraft} = useReservationData();
  const lastSyncedKeyRef = useRef(null);
  const lastSignalKeyRef = useRef(null);
  const lastErrorKeyRef = useRef(null);

  const {draftId, firstName, lastName, email, phone, service, partySize, date, timeSlot, selectedTimeSlotData, paymentSignal} = formData;

  useEffect(() => {
    // Adapter hôte sans support du brouillon : hook inerte (dégradation gracieuse)
    if (typeof syncReservationDraft !== 'function') return undefined;
    if (!SYNC_STEPS.includes(currentStep)) return undefined;
    if (!draftId) return undefined;

    // Coordonnées exploitables uniquement : sans contact valide, le brouillon
    // n'a aucune valeur pour le restaurateur
    if (!hasValidContact({firstName, lastName, email, phone})) return undefined;
    if (!service || !partySize || !date || !timeSlot) return undefined;

    let dateTime;
    try {
      dateTime = buildReservationDateTime({date, timeSlot, selectedTimeSlotData});
    } catch (err) {
      return undefined;
    }

    const payload = {
      draftId,
      // Le backend applique un $max sur le rang : un retour en arrière dans le
      // stepper ne fait jamais régresser l'étape d'abandon tracée
      lastStepReached: currentStep,
      dateTime,
      service,
      numberOfGuests: partySize,
      customerDetails: {
        firstName,
        lastName,
        email,
        phone,
      },
      language: currentLanguage,
    };

    // Le signal empreinte ne renvoie lastError que s'il a changé depuis le
    // dernier envoi : un renvoi identique écraserait ($set) une erreur plus
    // fraîche tracée entre-temps par un webhook Stripe
    const errorKey = JSON.stringify(paymentSignal?.lastError || null);
    if (paymentSignal) {
      payload.payment = errorKey !== lastErrorKeyRef.current ?
        paymentSignal :
        {state: paymentSignal.state, attempts: paymentSignal.attempts};
    }

    // Déduplication : pas de re-sync tant que le contenu n'a pas changé
    const signalKey = JSON.stringify(paymentSignal || null);
    const key = JSON.stringify([currentStep, dateTime, service, partySize, firstName, lastName, email, phone, signalKey]);
    if (key === lastSyncedKeyRef.current) return undefined;

    // Un changement de signal empreinte (erreur, tentative) part immédiatement ;
    // le tout premier envoi du parcours reste debouncé (frappe en cours)
    const isFirstSync = lastSyncedKeyRef.current === null;
    const delay = !isFirstSync && signalKey !== lastSignalKeyRef.current ? 0 : SYNC_DEBOUNCE_MS;

    const timer = setTimeout(() => {
      lastSyncedKeyRef.current = key;
      lastSignalKeyRef.current = signalKey;
      lastErrorKeyRef.current = errorKey;
      syncReservationDraft(payload).catch((err) => {
        // Fire-and-forget : un échec de traçage ne doit jamais remonter à l'UI
        logger.debug('Sync brouillon de réservation ignorée:', err?.message);
      });
    }, delay);

    return () => clearTimeout(timer);
  }, [
    syncReservationDraft,
    currentStep,
    draftId,
    firstName,
    lastName,
    email,
    phone,
    service,
    partySize,
    date,
    timeSlot,
    selectedTimeSlotData,
    currentLanguage,
    paymentSignal,
  ]);
}
